import type { ReactElement } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { usePortalSession } from './auth';
import { Login } from './auth/Login';
import { UserRegister } from './auth/UserRegister';
import { AppLayout } from './layouts/AppLayout';
import { LandingPage } from './pages/LandingPage';
import { ActorCatalog } from './pages/ActorCatalog';
import { SpaceDashboard } from './pages/SpaceDashboard';
import { SpaceEditor } from './pages/SpaceEditor';
import { SpacesList } from './pages/SpacesList';
import { Simulation } from './pages/Simulation';
import { SpaceTimeline } from './pages/SpaceTimeline';
import { TelemetryDashboard } from './pages/TelemetryDashboard';
import { UserProfile } from './pages/UserProfile';
import { ServiceStatusBanner } from './components/ServiceStatusBanner';

function ProtectedRoute(): ReactElement {
  const { session, loading } = usePortalSession();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

export default function App(): ReactElement {
  return (
    <div className="min-h-screen flex flex-col">
      <ServiceStatusBanner />
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Standalone auth pages — no shell */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<UserRegister />} />

        {/* Protected routes with AppLayout shell */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/spaces" element={<SpacesList />} />
            <Route path="/spaces/new" element={<SpaceEditor />} />
            <Route path="/spaces/:id" element={<SpaceEditor />} />
            <Route path="/spaces/:id/dashboard" element={<SpaceDashboard />} />
            <Route path="/spaces/:id/actors" element={<ActorCatalog />} />
            <Route path="/spaces/:id/timeline" element={<SpaceTimeline />} />
            <Route path="/spaces/:id/simulation" element={<Simulation />} />
            <Route path="/telemetry" element={<TelemetryDashboard />} />
            <Route path="/profile" element={<UserProfile />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/spaces" replace />} />
      </Routes>
    </div>
  );
}
